import React, { useEffect, useState } from 'react';
import api from '../../api/client';
import { useToast } from '../../context/ToastContext';

function Modal({ title, children, onClose }) {
  return (
    <div className="tr-modal-backdrop" onClick={onClose} role="presentation">
      <div className="tr-modal" onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1.25rem', borderBottom: '1px solid var(--border)' }}>
          <h2 style={{ margin: 0, fontSize: '1.1rem' }}>{title}</h2>
          <button type="button" className="tr-btn tr-btn-ghost" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        <div style={{ padding: '1.25rem' }}>{children}</div>
      </div>
    </div>
  );
}

export default function AdminLocations() {
  const toast = useToast();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [editLoc, setEditLoc] = useState(null);
  const [qrLoc, setQrLoc] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', floor: '', building: '' });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get('/locations/all');
    setLocations(data.locations || []);
  };

  useEffect(() => {
    (async () => {
      try {
        await load();
      } catch (e) {
        toast.error('Failed to load locations');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const openAdd = () => {
    setForm({ name: '', description: '', floor: '', building: '' });
    setAddOpen(true);
  };

  const submitAdd = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/locations', form);
      toast.success('Location created');
      setAddOpen(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create');
    } finally {
      setSaving(false);
    }
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    if (!editLoc) return;
    setSaving(true);
    try {
      await api.put(`/locations/${editLoc.id}`, {
        name: form.name,
        description: form.description,
        floor: form.floor,
        building: form.building,
      });
      toast.success('Location updated');
      setEditLoc(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (loc) => {
    try {
      await api.put(`/locations/${loc.id}`, { is_active: !loc.is_active });
      toast.success(loc.is_active ? 'Location deactivated' : 'Location activated');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed');
    }
  };

  const regenerate = async (id) => {
    try {
      await api.post(`/locations/${id}/regenerate-qr`);
      toast.success('QR code regenerated');
      await load();
      const { data } = await api.get('/locations/all');
      const updated = data.locations.find((l) => l.id === id);
      if (updated) setQrLoc(updated);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to regenerate');
    }
  };

  const downloadQr = () => {
    if (!qrLoc?.qr_code) return;
    const link = document.createElement('a');
    link.href = qrLoc.qr_code;
    link.download = `qr-${qrLoc.name.replace(/\s+/g, '-')}.png`;
    link.click();
  };

  const copyUrl = async () => {
    if (!qrLoc?.qr_url) return;
    try {
      await navigator.clipboard.writeText(qrLoc.qr_url);
      toast.success('Link copied');
    } catch {
      toast.error('Could not copy');
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
        <div className="tr-spinner" />
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
        <h1 style={{ margin: 0 }}>Locations &amp; QR</h1>
        <button type="button" className="tr-btn tr-btn-primary" onClick={openAdd}>
          Add Location
        </button>
      </div>

      {locations.length === 0 ? (
        <div className="tr-card" style={{ padding: '3rem', textAlign: 'center', marginTop: '1.5rem' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📍</div>
          <h2 style={{ marginTop: 0 }}>No locations yet</h2>
          <p style={{ color: 'var(--text-muted)' }}>Add your first location to generate a QR code for visitors.</p>
          <button type="button" className="tr-btn tr-btn-primary" style={{ marginTop: '1rem' }} onClick={openAdd}>
            Add Location
          </button>
        </div>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
            gap: '1.25rem',
            marginTop: '1.5rem',
          }}
        >
          {locations.map((loc) => (
            <div key={loc.id} className="tr-card" style={{ padding: '1.25rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem' }}>
                <div>
                  <h3 style={{ margin: '0 0 0.35rem', fontSize: '1.05rem' }}>{loc.name}</h3>
                  <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                    {loc.floor} · {loc.building}
                  </div>
                </div>
                <span
                  style={{
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    background: 'var(--primary-soft)',
                    color: 'var(--primary)',
                    padding: '0.2rem 0.5rem',
                    borderRadius: 6,
                  }}
                >
                  {loc.ticket_count ?? 0} tickets
                </span>
              </div>
              <p style={{ fontSize: '0.9rem', color: 'var(--text-muted)', minHeight: 40 }}>{loc.description || '—'}</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.5rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem' }}>
                  <input type="checkbox" checked={loc.is_active} onChange={() => toggleActive(loc)} />
                  Active
                </label>
                <button type="button" className="tr-btn tr-btn-outline" style={{ padding: '0.35rem 0.65rem', fontSize: '0.85rem' }} onClick={() => setQrLoc(loc)}>
                  View QR
                </button>
                <button
                  type="button"
                  className="tr-btn tr-btn-ghost"
                  style={{ padding: '0.35rem 0.65rem', fontSize: '0.85rem' }}
                  onClick={() => {
                    setEditLoc(loc);
                    setForm({
                      name: loc.name,
                      description: loc.description || '',
                      floor: loc.floor || '',
                      building: loc.building || '',
                    });
                  }}
                >
                  Edit
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {addOpen && (
        <Modal title="Add location" onClose={() => setAddOpen(false)}>
          <form onSubmit={submitAdd}>
            <label className="tr-label">Name</label>
            <input className="tr-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            <label className="tr-label" style={{ marginTop: '0.75rem' }}>
              Description
            </label>
            <textarea className="tr-input" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            <label className="tr-label" style={{ marginTop: '0.75rem' }}>
              Floor
            </label>
            <input className="tr-input" value={form.floor} onChange={(e) => setForm({ ...form, floor: e.target.value })} required />
            <label className="tr-label" style={{ marginTop: '0.75rem' }}>
              Building
            </label>
            <input className="tr-input" value={form.building} onChange={(e) => setForm({ ...form, building: e.target.value })} required />
            <button type="submit" className="tr-btn tr-btn-primary" style={{ marginTop: '1rem', width: '100%' }} disabled={saving}>
              {saving ? 'Saving…' : 'Create'}
            </button>
          </form>
        </Modal>
      )}

      {editLoc && (
        <Modal title="Edit location" onClose={() => setEditLoc(null)}>
          <form onSubmit={submitEdit}>
            <label className="tr-label">Name</label>
            <input className="tr-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            <label className="tr-label" style={{ marginTop: '0.75rem' }}>
              Description
            </label>
            <textarea className="tr-input" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            <label className="tr-label" style={{ marginTop: '0.75rem' }}>
              Floor
            </label>
            <input className="tr-input" value={form.floor} onChange={(e) => setForm({ ...form, floor: e.target.value })} required />
            <label className="tr-label" style={{ marginTop: '0.75rem' }}>
              Building
            </label>
            <input className="tr-input" value={form.building} onChange={(e) => setForm({ ...form, building: e.target.value })} required />
            <button type="submit" className="tr-btn tr-btn-primary" style={{ marginTop: '1rem', width: '100%' }} disabled={saving}>
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </form>
        </Modal>
      )}

      {qrLoc && (
        <Modal title="QR code" onClose={() => setQrLoc(null)}>
          <div style={{ textAlign: 'center' }}>
            {qrLoc.qr_code && <img src={qrLoc.qr_code} alt="QR" style={{ maxWidth: '100%', height: 'auto', borderRadius: 8 }} />}
            <p style={{ wordBreak: 'break-all', fontSize: '0.85rem', color: 'var(--text-muted)' }}>{qrLoc.qr_url}</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', justifyContent: 'center' }}>
              <button type="button" className="tr-btn tr-btn-primary" onClick={copyUrl}>
                Copy link
              </button>
              <button type="button" className="tr-btn tr-btn-outline" onClick={downloadQr}>
                Download PNG
              </button>
              <button type="button" className="tr-btn tr-btn-outline" onClick={() => regenerate(qrLoc.id)}>
                Regenerate
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
